import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../services/supabaseClient';

/**
 * Auth hook for RoofClaw admin.
 *
 * Handles Supabase Auth sessions with localStorage persistence.
 * Falls back to legacy password auth if Supabase Auth fails.
 *
 * Returns:
 *   - session: Supabase session object (null if using legacy auth)
 *   - user: Supabase user object (null if using legacy auth)
 *   - password: The admin password (for API calls during migration)
 *   - authenticated: boolean
 *   - loading: boolean (true during initial session check)
 *   - method: 'jwt' | 'legacy' | null
 *   - error: string | null
 *   - login(email, password): attempts Supabase Auth, falls back to legacy
 *   - logout(): clears session
 *   - getAuthHeaders(): returns headers object for API calls
 */
export function useAuth() {
    const [session, setSession] = useState(null);
    const [user, setUser] = useState(null);
    const [password, setPassword] = useState(null);
    const [authenticated, setAuthenticated] = useState(false);
    const [loading, setLoading] = useState(true);
    const [method, setMethod] = useState(null);
    const [error, setError] = useState(null);

    // Check for existing session on mount
    useEffect(() => {
        const checkSession = async () => {
            try {
                if (!supabase) { setLoading(false); return; }
                const { data: { session: existingSession } } = await supabase.auth.getSession();
                // Always restore password from sessionStorage if available
                // (needed for API calls during migration — Phase B will remove this)
                const storedPw = sessionStorage.getItem('admin_password');
                if (existingSession) {
                    setSession(existingSession);
                    setUser(existingSession.user);
                    if (storedPw) setPassword(storedPw);
                    setAuthenticated(true);
                    setMethod('jwt');
                } else if (storedPw) {
                    // Legacy password only — no JWT session
                    setPassword(storedPw);
                    setAuthenticated(true);
                    setMethod('legacy');
                }
            } catch (e) {
                console.error('Auth session check failed:', e);
            } finally {
                setLoading(false);
            }
        };

        checkSession();

        // Listen for auth state changes (auto-refresh, logout)
        // Guard: supabase is null when env vars are missing (CI, local dev without .env)
        if (!supabase) return;

        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (_event, newSession) => {
                setSession(newSession);
                setUser(newSession?.user || null);
                if (newSession) {
                    setAuthenticated(true);
                    setMethod('jwt');
                } else if (!password) {
                    setAuthenticated(false);
                    setMethod(null);
                }
            }
        );

        return () => subscription?.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps — mount-only: session check + auth listener setup
    }, []);

    const login = useCallback(async (email, inputPassword) => {
        setError(null);
        setLoading(true);

        try {
            // Try Supabase Auth first
            if (email) {
                const { data, error: authError } = await supabase.auth.signInWithPassword({
                    email,
                    password: inputPassword,
                });

                if (data?.session && !authError) {
                    setSession(data.session);
                    setUser(data.user);
                    setPassword(inputPassword); // Keep for legacy API calls during migration
                    sessionStorage.setItem('admin_password', inputPassword); // ClientDetail reads this
                    setAuthenticated(true);
                    setMethod('jwt');
                    setLoading(false);
                    return { success: true, method: 'jwt' };
                }
            }

            // Fall back to legacy password auth
            const res = await fetch(`/api/get-admin-pipeline?password=${encodeURIComponent(inputPassword)}`);
            if (res.ok) {
                setPassword(inputPassword);
                sessionStorage.setItem('admin_password', inputPassword);
                setAuthenticated(true);
                setMethod('legacy');
                setLoading(false);
                return { success: true, method: 'legacy' };
            }

            setError('Invalid email or password');
            setLoading(false);
            return { success: false };
        } catch (e) {
            setError(e.message || 'Login failed');
            setLoading(false);
            return { success: false };
        }
    }, []);

    const logout = useCallback(async () => {
        // Clear local state first — even if signOut fails, user is logged out locally
        sessionStorage.removeItem('admin_password');
        setSession(null);
        setUser(null);
        setPassword(null);
        setAuthenticated(false);
        setMethod(null);
        try {
            await supabase.auth.signOut();
        } catch (e) {
            console.error('Supabase signOut failed:', e);
            // Local state already cleared — user is still logged out
        }
    }, []);

    const getAuthHeaders = useCallback(() => {
        const headers = { 'Content-Type': 'application/json' };
        if (session?.access_token) {
            headers['Authorization'] = `Bearer ${session.access_token}`;
            // Also send legacy password during migration (Phase B compatibility)
            if (password) headers['x-admin-password'] = password;
        } else if (password) {
            headers['x-admin-password'] = password;
        }
        return headers;
    }, [session, password]);

    const resetPassword = useCallback(async (email) => {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
            redirectTo: `${window.location.origin}/admin`,
        });
        return { success: !error, error: error?.message };
    }, []);

    return {
        session,
        user,
        password,
        authenticated,
        loading,
        method,
        error,
        login,
        logout,
        getAuthHeaders,
        resetPassword,
    };
}
